<template>
  <div>
    <div v-if="messageError.length > 0">
      <h1>{{ messageError }}</h1>
    </div>
    <div v-else class="message-wrapper" ref="messageWrapper">
      <transition name="fade">
        <div v-if="loadingStatus" class="loading">
          <div class="loading-icon">
            <fa icon="sync-alt" spin/>
          </div>
          <div class="loading-text">
            Loading message...
          </div>
        </div>
      </transition>
      <div v-show="!loadingStatus" class="message-section">
        <div v-if="!lightMode" :class="stickyHeaderClass">
          <template v-if="!external">
            <b-collapse :visible="desktop" :id="'actions-collapse' + message.id">
              <div class="message-header bg-secondary">
                <ul class="message-actions">
                  <MessageActions v-if="message.id" :messageIds="[message.id]" :hasTicket="message.hasTicket" :inbox="false" :mailboxId="message.mailbox_id" :message="message" />
                </ul>
              </div>
            </b-collapse>
          </template>
          <div v-if="message.id" class="ticket-status-user" ref="ticketStatusUser" :class="statusVariant('ticket')">
            <a v-if="!external" href="#" v-b-toggle="'actions-collapse' + message.id" class="actions-toggle">
              <fa icon="bars"/>
            </a>
            <transition name="fade">
              <div v-if="messageUpdates > 0 && !external" @click="getMessage" class="message-updates">
                <b-badge variant="warning"> <fa icon="sync-alt" /> Updates available </b-badge>
              </div>
            </transition>
            <div v-if="message.user && !external" v-b-tooltip.hover.bottom :title="'Assigned to ' + message.user.full_name" class="user-icon-wrapper">
              <img :src="message.user.avatar_url" class="user-icon">
            </div>
            <div class="status">
              <b-badge :variant="statusVariant('badge')">{{ message.status }}</b-badge>
            </div>
            <div v-if="message.hasTicket" @click="scrollToTicket" v-b-tooltip.hover.bottom title="Go to ticket" class="ticket-id">
              <span v-if="message.merged_ticket">
                #{{message.merged_ticket.id}}
              </span>
              <span v-else>
                #{{message.ticket.id}}
              </span>
            </div>
          </div>
          <div class="subject-wrapper subject-sticky clearfix" ref="subjectWrapper">
            <div class="subject">
              {{ message.subject }}
            </div>
          </div>
        </div>
        <div class="subject-wrapper subject-mobile clearfix">
          <div class="subject">
            {{ message.subject }}
          </div>
        </div>
        <Tags v-if="!external" :tags=message.tags :message_id="message.id" />
        <template v-if="!external">
          <NewNote v-show="showNote" :entity_with_note_type="'Message'" :entity_with_note_id="message.id"/>
          <NewMessage v-if="message.mailbox && showReply" :mailbox="message.mailbox" :parentMessage="message" parent="message" ref="newMessageComponent"/>
        </template>
        <div class="items-wrapper">
          <MessageItem v-for="item in message.message_items"
                       :key="item.created_at"
                       :item="item"
                       :mailbox_id="message.mailbox_id"
                       :draft_only="draftOnly"
                       :show_message_id="message.merged"
                       :external="external"
                       :draft_count="message.draftCount"/>
        </div>
      </div>
      <template v-if="ticketObject.tasks && parent !== 'ticket' && !lightMode">
        <b-btn v-b-tooltip.hover title="View tasks" v-b-toggle="'collapse' + ticketObject.id" :class="statusVariant('ticket')" class="task-button" id="ticket-toggle">
          <span class="oi oi-chevron-top"></span>
        </b-btn>
        <b-collapse visible :id="'collapse' + ticketObject.id" class="mt-2" >
          <Ticket :name="'Ticket'"
                  :ticket="ticketObject"
                  :default_board_id="message.mailbox.default_board_id"
                  :parent="'message'"
                  :external="external"/>
        </b-collapse>
      </template>
      <a v-if="!external && parent !== 'ticket'" @click="toggleReply" href="#" title="Reply" class="reply-float">
        <span class="reply-float-arrow">
          <fa icon="reply"/>
        </span>
      </a>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import MessageActions from './MessageActions'
import MessageItem from './MessageItem'
import NewMessage from './NewMessage'
import NewNote from './NewNote'
import Tags from './Tags'
import cable from '@/action_cable'
import _ from 'lodash'
export default {
  name: 'Message',
  components: {
    MessageActions,
    MessageItem,
    NewMessage,
    NewNote,
    Ticket: () => import('./Ticket'), // prevents Message/Ticket import paradox
    Tags
  },
  props: {
    id: [String, Number],
    parent: String,
    lightMode: false,
    external: Boolean
  },
  data () {
    return {
      showNote: false,
      showReply: false,
      ticketObject: {},
      interval: null,
      updates: 0,
      connection: null,
      showMessageId: false,
      desktop: false
    }
  },
  created () {
    if (window.innerWidth > 767) {
      this.desktop = true
    }
    this.getMessage().then(() => {
      if (!this.external) {
        this.createConnection()
      }
    })
    window.addEventListener('resize', this.titleWidth)
  },
  mounted () {
    this.$root.$on('destroyMessageForm', () => {
      this.showReply = false
    })

    this.$root.$on('sortMergedItems', () => {
      this.showMessageId = true
    })

    this.$root.$on('noteWasAdded', () => {
      this.showNote = false
      this.reloadMessage()
    })

    this.$root.$on('ticketWasCreated', () => {
      this.getMessage()
    })

    this.$root.$on('toggleNote', () => {
      if (!this.showNote && this.showReply) {
        this.showReply = !this.showReply
      }
      this.showNote = !this.showNote
    })
    this.$root.$on('toggleReply', () => {
      if (!this.showReply) {
        if (this.showNote) {
          this.showNote = !this.showNote
        }
        this.connection.startEdit()
      } else {
        this.connection.stopEdit()
      }
      this.showReply = !this.showReply
      window.scrollTo(0, 0)
    })

    this.$root.$on('draftEdit', () => {
      if (!this.showReply) {
        this.$root.$emit('toggleReply')
      }
    })
    this.$root.$on('noteEdit', (item) => {
      this.showNote = true
    })
    this.$root.$on('closeNoteEdit', (item) => {
      this.showNote = false
    })
  },

  beforeDestroy () {
    clearInterval(this.interval)
    this.setDefaultMessage()
    this.clearListeners()
    if (!this.external) {
      this.connection.unsubscribe()
    }
    window.removeEventListener('resize', this.titleWidth)
  },

  computed: {
    ...mapGetters(['currentUser', 'availableUsers', 'message', 'messageUpdates', 'messageSending', 'loadingStatus', 'messageError']),

    draftOnly () {
      if (this.message.source_id === null || this.message.source_id === '') {
        return true
      } else {
        return false
      }
    },
    stickyHeaderClass () {
      if (this.external) {
        return 'sticky-header-external'
      } else {
        return 'sticky-header'
      }
    }
  },

  methods: {
    ...mapActions(['loadMessage', 'reloadMessage']),
    ...mapMutations(['setDefaultDraft', 'setDefaultMessage', 'incUpdates', 'resetUpdates', 'setSendingStatus']),
    showModal () {
      this.$root.$emit('bv::show::modal', 'newTicket')
    },
    all_messages (mailbox) {
      this.$http.get('/messages')
        .then(response => this.showUsers(response))
        .catch(e => { console.log(e.response) })
    },

    async getMessage () {
      this.loadMessage({messageId: this.id, externalToken: this.$route.params.token})
        .then(response => {
          this.getTicketObject()
        })
        .catch(error => {
          console.log(error)
        })
    },

    statusVariant (type) {
      var variant
      switch (this.message.status) {
        case 'active':
          variant = 'info'
          break
        case 'closed':
          variant = 'dark'
          break
        default:
          variant = 'secondary'
      }
      if (type === 'ticket') {
        return 'bg-' + variant
      } else {
        return variant
      }
    },
    getTicketObject () {
      if (this.parent === 'ticket' || (!this.message.hasTicket && !this.message.merged_ticket)) {
        return false
      }
      let ticketId = this.message.merged_ticket ? this.message.merged_ticket.id : this.message.ticket.id
      let params = {message_id: this.message.id}
      if (this.external) {
        params.external_token = this.$route.params.token
      }
      this.$http
        .get(`/tickets/${ticketId}`, { params: params })
        .then(response => {
          this.ticketObject = response.data
        })
        .catch(e => { console.log(e.response) })
    },
    scrollToTicket () {
      var navContent = document.getElementById('vue-navcontent')
      if (this.external) {
        window.scrollTo(0, navContent.scrollHeight)
      } else {
        navContent.scrollTo(0, navContent.scrollHeight)
      }
    },

    clearListeners () {
      this.$root.$off('toggleReply')
      this.$root.$off('noteWasAdded')
      this.$root.$off('ticketWasCreated')
    },

    createConnection () {
      let channelId
      if (this.id) {
        channelId = this.id
      } else {
        channelId = this.$route.params.id
      }
      this.connection = cable.subscriptions.create({channel: 'MessagesChannel', id: channelId}, {
        connected: function () {
        },
        received: function (response) {
          if (response.message === 'MessageView created') {
            this.$set(this.message.message_state.viewers, response.data.user_id, response.data)
          } else if (response.message === 'MessageView destroyed') {
            this.$delete(this.message.message_state.viewers, response.data.user_id)
          } else if (response.message === 'MessageEdit created') {
            this.$set(this.message.message_state.editors, response.data.user_id, response.data)
          } else if (response.message === 'MessageEdit destroyed') {
            this.$delete(this.message.message_state.editors, response.data.user_id)
          } else {
            this.incUpdates()
          }
        }.bind(this),
        startEdit: function () {
          this.perform('start_edit')
        },
        stopEdit: function () {
          this.perform('stop_edit')
        }
      })
    },
    toggleReply () {
      this.scrollTop()
      this.$root.$emit('toggleReply')
    },
    scrollTop () {
      document.getElementById('vue-navcontent').scrollTo(0, 0)
    },
    titleWidth: _.debounce(function () {
      if (!this.message.id || !this.$refs.messageWrapper) {
        return false
      }
      let fullW = this.$refs.messageWrapper.offsetWidth
      let statusW = this.$refs.ticketStatusUser.offsetWidth
      let titleW = fullW - statusW - 12
      this.$refs.subjectWrapper.maxWidth = (titleW + 'px')
    }, 100)
  },

  updated: function () {
    this.$nextTick(function () {
      this.titleWidth()
    })
  },

  beforeRouteLeave: function (to, from, next) {
    if (this.messageSending) {
      let answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.setSendingStatus(false)
      }
      next(answer)
    } else {
      next()
    }
  },

  watch: {
    id (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getMessage().then(() => {
          if (!this.external) {
            this.createConnection()
          }
        })
      }
    }
  }

}
</script>

<style scoped>
  .message-header {
    width: 100%;
    display: block;
    background: #6F6F6F;
    height: 55px;
    padding: 0;
    }
  ul {
    list-style: none;
    margin: 0;
    height: 54px;
    float: left;
    position: relative;
    z-index: 101;
    margin-left: 8px;
    padding: 7px 5px 6px 0;
    width: 100%;
  }
  .subject {
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    overflow: hidden;
    position: relative;
    display: inline-block;
    max-width: 100%;
    font-size: 24px;
    font-weight: 500;
    float: left;
    margin-right: 10px;
    margin-left: 10px;
  }
  div.subject-wrapper {
    padding-top: 10px;
    text-align: left;
    display: inline;
    float: left;
  }
  div.subject-wrapper.subject-mobile {
    display: none;
  }
  div.ticket-status-user {
    display: flex;
    float: right;
    height: 50px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    margin-left: 10px;
  }
  div.ticket-status-user.bg-info, div.ticket-status-user.bg-info span.badge-info {
    background-color: #57B2CD !important;
  }
  div.ticket-status-user > .ticket-id, .status {
    display: inline-block;
    color: #FFF;
  }
  .status {
    font-size: 20px;
    height: 100%;
    line-height: 45px;
    padding-right: 5px;
    padding-left: 5px;
    text-transform: uppercase;
  }
  .ticket-id {
    font-size: 30px;
    padding-left: 5px;
    padding-right: 5px;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    background-color: inherit;
    cursor: pointer;
  }
  .user-icon-wrapper {
    border-bottom-left-radius: 5px;
    background-color: inherit;
  }
  .user-icon {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    margin-top: 7px;
    margin-left: 10px;
    margin-right: 10px;
  }
  .ticket-id:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  .collapsed .oi-chevron-top {
    transform: translateY(2px) rotateX(180deg);
  }
  .task-button {
    height: 40px;
    border-radius: 50%;
    border: none;
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .sticky-header {
    top: 0px;
    position: sticky;
    z-index: 1000;
    padding-bottom: 50px;
    background-color: #ffffffe0;
  }
  .sticky-header-external {
    top: 58px;
    position: sticky;
    z-index: 1000;
    padding-bottom: 50px;
    background-color: #ffffffe0;
  }
  .actions-toggle {
    color: #fff;
    padding-top: 8px;
    padding-left: 15px;
    padding-right: 5px;
    line-height: 32px;
    font-size: 20px;
    cursor: pointer;
  }
  .actions-toggle:hover {
    display: block;
  }
  .gray {
    background: #f2f3f4;
   }
  .green {
     background: #4dbd7c;
   }
  .blue {
     background: #2f44d6;
   }
  .orange {
    background: #f98b33;
   }
  .purple {
    background: #7e81e7;
  }
  .red {
    background: #e02e28;
  }
  .message-updates {
    font-size: 20px;
    height: 100%;
    line-height: 45px;
    padding-left: 10px;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity .75s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  .fade-transition {
    transition: opacity 8000ms ease;
  }
  .fade-transition.loading {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 15px;
    right: 15px;
  }
  .fade-enter,
  .fade-leave {
    opacity: 0;
  }
  .loading {
    height: 20vh;
    width: 100%;
    padding: 5vw;
    text-align: center;
    font-size: calc(~"15px + 1.5vw");
    background-color: #fff;
    position: absolute;
    z-index: 1001;
  }
  .message-section {
    transition: all 1s ease;
    transition: opacity 1s;
  }
  .message-wrapper {
    transition: height 1s ease;
    min-height: 20vh;
    min-width: 100%;
  }
  .reply-float {
    position: fixed;
    right: 25px;
    bottom: 25px;
    background: #57B2CD;
    color: #fff;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    z-index: 1000;
    box-shadow: 2px 2px 10px 0px #7b7b7b
  }
  span.reply-float-arrow {
    top: 5px;
    position: relative;
    font-size: 20px;
  }
  @media (max-width: 767px) {
    .sticky-header, .sticky-header-external {
      padding-bottom: 0;
    }
    .subject {
      font-size: 14px;
    }
    div.subject-wrapper.subject-sticky {
      display: none
    }
    div.subject-wrapper.subject-mobile {
      display: inline;
    }
    div.ticket-status-user {
      display: flex;
      float: right;
      height: 40px;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
    }
    .user-icon {
      width: 25px;
      height: 25px;
      border-radius: 50%;
      margin-top: 6px;
    }
    .status {
      font-size: 18px;
      line-height: 36px;
      padding: 0px;
    }
    .ticket-id {
      font-size: 18px;
      line-height: 38px;
    }
    .actions-toggle {
      font-size: 18px;
      line-height: 38px;
      padding-top: 0px;
      padding-right: 5px;
    }
  }
</style>
<style>
div.message-section > form.new-message-form.text-left {
  margin-top: 5px;
}
@media (max-width: 480px) {
  .dropdown.v-select.single.searchable .dropdown-menu {
  max-height: 100px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Message.vue